import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useValidatableFormFieldFocus } from '../../../hooks';
import { FormFieldWrap, Input } from '@zooplus/ui-components';
import { NumberFormFieldValidator } from '../../../hooks/validation/validator/NumberFormFieldValidator';
import {
  FieldPropsExternalValidation,
  FieldPropsWithExternalValue,
  FieldPropsWithOnChange,
} from './types';
import {
  useFormFieldMainValidation,
  useFormFieldValidationWithOnValueChangeValidation,
} from '../../../hooks';
import InputWithLoader from '../input-with-loader/InputWithLoader';
import { CompositeOrderedValidator } from '../../../hooks/validation/validator/CompositeOrderedValidator';

type CustomerNumberFieldProps = FieldPropsWithExternalValue &
  FieldPropsWithOnChange &
  FieldPropsExternalValidation;

const CustomerNumberField: React.FC<CustomerNumberFieldProps> = ({
  externalValue,
  onChange,
}: any) => {
  const { t } = useTranslation();

  const label = t('form.fields.customer_number.label');
  const placeholder = t('form.fields.customer_number.placeholder');
  const isRequired = false;
  const minLength = 1;
  const maxLength = 15;
  const [isProcessing, setIsProcessing] = useState(false);
  const inputWrapperElementRef = useRef<HTMLDivElement>(null);
  const basicFieldValidator = new NumberFormFieldValidator(isRequired, minLength, maxLength);

  const [value, setValue] = useState<string>('');
  useEffect(() => {
    externalValue && setValue('' + externalValue);
  }, [externalValue]);
  useEffect(() => {
    onChange(value);
  }, [value]);

  const { hasError: staticValidatorHasError } = useFormFieldValidationWithOnValueChangeValidation(
    basicFieldValidator,
    { value },
  );
  const {
    hasError: fullValidatorHasError,
    errorTags: fullValidationErrorTags,
    validate: runFullValidation,
  } = useFormFieldMainValidation(new CompositeOrderedValidator(basicFieldValidator), { value });
  const [hasError, setHasError] = useState(false);
  useEffect(() => {
    setHasError(staticValidatorHasError || fullValidatorHasError);
  }, [staticValidatorHasError, fullValidatorHasError]);
  const [errorMessage, setErrorMessage] = useState('');
  useEffect(() => {
    const key = fullValidationErrorTags[0] || 'general';
    setErrorMessage(t(`form.fields.customer_number.error_messages.${key}`));
  }, [fullValidationErrorTags]);
  useValidatableFormFieldFocus({
    formFieldSelector: 'input',
    formFieldWrapperElementRef: inputWrapperElementRef,
  });

  const updateValueCallback = useCallback((evt) => setValue(evt.target.value), []);
  const onBlurCallback = useCallback(async () => {
    setIsProcessing(true);
    await runFullValidation();
    setIsProcessing(false);
  }, [runFullValidation]);

  return (
    <div
      data-test="contactForm.field.customerId"
      data-form-control-type="NUMBER"
      className="contactForm__field">
      <FormFieldWrap
        labelFor="customer-id"
        isRequired={isRequired}
        labelText={label}
        errorMessage={hasError && errorMessage}
        ref={inputWrapperElementRef}>
        <InputWithLoader isLoading={isProcessing} isDisabled={isProcessing}>
          <Input
            hasError={hasError}
            onChange={updateValueCallback}
            isDisabled={isProcessing}
            inputProps={{
              id: 'customer-id',
              placeholder: placeholder,
              name: 'customerNumber',
              type: 'text',
              readOnly: !!externalValue,
              minLength: minLength,
              maxLength: maxLength,
              value: value as string,
              onBlur: onBlurCallback,
              onKeyDown: NumberFormFieldValidator.checkPressedKeySequenceIsValid,
            }}
          />
        </InputWithLoader>
      </FormFieldWrap>
    </div>
  );
};

export default CustomerNumberField;
